import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  MapPin, ChevronLeft, ChevronRight, MessageCircle, CalendarCheck,
  FileText, Check, X, ArrowLeft, Loader2,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useSettings, waLink } from "../hooks/useSettings";
import { SPEC_FIELDS } from "../lib/format";
import { Calendar } from "../components/ui/calendar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useI18n } from "../context/I18nContext";

function toRange(bp) {
  return { from: new Date(bp.start + "T00:00:00"), to: new Date(bp.end + "T00:00:00") };
}

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  const { t, money, specLabel, lang } = useI18n();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState({ customer_name: "", email: "", phone: "", guests: 1, message: "" });
  const [bookingType, setBookingType] = useState("reservation");
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    api.get(`/listings/${id}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
  }, [id, lang]);

  const blockedMatchers = useMemo(
    () => (item?.blockedPeriods || []).map(toRange),
    [item]
  );

  const checkAvailability = async () => {
    if (!start || !end) { toast.error(t("listing.tSelectDates")); return; }
    if (end < start) { toast.error(t("listing.tDepartAfter")); return; }
    setChecking(true);
    try {
      const { data } = await api.post(`/listings/${id}/availability`, { start, end });
      setAvail(data.available);
      toast[data.available ? "success" : "error"](data.available ? t("listing.tAvailable") : t("listing.tUnavailable"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setChecking(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        listing_id: id,
        ...form,
        guests: Number(form.guests),
        start_date: bookingType === "reservation" ? start || null : null,
        end_date: bookingType === "reservation" ? end || null : null,
        type: bookingType,
      });
      toast.success(t("listing.tSent"));
      setDialogOpen(false);
      setForm({ customer_name: "", email: "", phone: "", guests: 1, message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound)
    return (
      <div className="min-h-screen pt-[72px] flex flex-col items-center justify-center gap-4">
        <p className="font-display text-4xl text-[#F5F0E1]">{t("listing.notFound")}</p>
        <Link to="/" className="text-[#D4AF37] tracking-widest uppercase text-sm">{t("listing.backHome")}</Link>
      </div>
    );

  if (!item)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );

  const waText = t("listing.waText", { title: item.title, location: item.location });
  const specFields = SPEC_FIELDS[item.category] || [];

  const openDialog = (type) => { setBookingType(type); setDialogOpen(true); };

  return (
    <div className="min-h-screen pt-[72px]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-8">
        <Link
          to={`/${item.category === "cars" ? "voitures" : item.category === "yachts" ? "yachts" : "villas"}`}
          onClick={(e) => {
            if (window.history.state && window.history.state.idx > 0) {
              e.preventDefault();
              navigate(-1);
            }
          }}
          className="inline-flex items-center gap-2 text-[#93A099] hover:text-[#D4AF37] text-sm mb-6 transition-colors"
          data-testid="back-link"
        >
          <ArrowLeft className="w-4 h-4" /> {t("listing.backToCatalog")}
        </Link>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
          <div className="relative aspect-[16/10] rounded-3xl overflow-hidden bg-[#0E0D0A]/60 backdrop-blur-xl">
            <motion.img key={activeImg} initial={{ opacity: 0.4, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              src={item.images[activeImg]} alt={item.title} className="w-full h-full object-cover" data-testid="gallery-main-image" />
            <span className="absolute top-5 left-5 text-[10px] tracking-luxe uppercase bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-full text-[#E5C378] border border-[#D4AF37]/20">
              {t(`badge.${item.category}`)}
            </span>
            <span className="absolute bottom-5 left-5 text-[11px] font-mono2 bg-black/75 backdrop-blur-md px-3 py-1.5 rounded-full text-[#F5F0E1] border border-white/10" data-testid="gallery-counter">
              {activeImg + 1} / {item.images.length}
            </span>
            {item.images.length > 1 && (
              <>
                <button onClick={() => setActiveImg((activeImg - 1 + item.images.length) % item.images.length)} data-testid="gallery-prev" className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/70 backdrop-blur-md border border-white/10 flex items-center justify-center text-[#F5F0E1] hover:bg-[#B8912A] hover:text-black transition-colors">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button onClick={() => setActiveImg((activeImg + 1) % item.images.length)} data-testid="gallery-next" className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/70 backdrop-blur-md border border-white/10 flex items-center justify-center text-[#F5F0E1] hover:bg-[#B8912A] hover:text-black transition-colors">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          <div className="grid grid-cols-4 lg:grid-cols-2 gap-3 content-start lg:max-h-[500px] lg:overflow-y-auto lg:pr-1">
            {item.images.map((img, i) => (
              <button key={i} onClick={() => setActiveImg(i)} data-testid={`gallery-thumb-${i}`}
                className={`aspect-square rounded-2xl overflow-hidden border-2 bg-[#0E0D0A]/60 transition-all ${activeImg === i ? "border-[#D4AF37]" : "border-white/[0.06] opacity-80 hover:opacity-100"}`}>
                <img src={img} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10 mt-12">
          <div>
            <div className="flex items-center gap-1.5 text-[#93A099] text-sm mb-3">
              <MapPin className="w-4 h-4 text-[#D4AF37]" /> {item.location}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-[#F5F0E1] leading-tight">{item.title}</h1>
            <p className="text-[#C7D0CA] leading-relaxed mt-6 text-base">{item.description}</p>

            {/* Specs */}
            <div className="mt-10">
              <h2 className="font-display text-3xl text-[#F5F0E1] mb-6">{t("listing.characteristics")}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-[#D4AF37]/10 rounded-3xl overflow-hidden border border-white/[0.06]">
                {specFields.filter((f) => item.specs?.[f]).map((f) => (
                  <div key={f} className="bg-[#0E0D0A]/80 p-5" data-testid={`spec-${f}`}>
                    <div className="text-[10px] tracking-widest uppercase text-[#7E8C84] mb-1.5">{specLabel(f)}</div>
                    <div className="font-mono2 text-sm text-[#D4AF37]">{String(item.specs[f])}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Availability calendar */}
            <div className="mt-10">
              <h2 className="font-display text-3xl text-[#F5F0E1] mb-2">{t("listing.availability")}</h2>
              <p className="text-[#93A099] text-sm mb-6">{t("listing.availabilityNote")}</p>
              <div className="inline-block rounded-3xl border border-white/[0.08] bg-[#0E0D0A]/60 backdrop-blur-xl p-4">
                <Calendar
                  mode="single"
                  numberOfMonths={1}
                  disabled={blockedMatchers}
                  modifiers={{ blocked: blockedMatchers }}
                  modifiersClassNames={{ blocked: "line-through text-red-400/60" }}
                  className="text-[#F5F0E1]"
                />
              </div>
            </div>
          </div>

          {/* Sticky booking card */}
          <div>
            <div className="lg:sticky lg:top-24 rounded-3xl border border-[#D4AF37]/20 bg-[#0E0D0A]/60 backdrop-blur-xl p-6 shadow-2xl">
              <div className="flex items-end justify-between pb-5 border-b border-white/[0.08]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#7E8C84]">{t("common.from")}</div>
                  <div className="font-display text-3xl text-[#D4AF37]" style={{ fontWeight: 1000 }}>{money(item.customerPrice, item.priceUnit)}</div>
                </div>
              </div>

              {item.priceUnit !== "request" && (
                <div className="mt-5 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-1.5">{t("listing.arrival")}</label>
                      <input type="date" value={start} data-testid="detail-start-date" onChange={(e) => { setStart(e.target.value); setAvail(null); }} className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-[#F5F0E1] [color-scheme:dark] outline-none focus:border-[#D4AF37]" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-widest text-[#7E8C84] block mb-1.5">{t("listing.departure")}</label>
                      <input type="date" value={end} data-testid="detail-end-date" onChange={(e) => { setEnd(e.target.value); setAvail(null); }} className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-2.5 text-sm text-[#F5F0E1] [color-scheme:dark] outline-none focus:border-[#D4AF37]" />
                    </div>
                  </div>
                  <button onClick={checkAvailability} disabled={checking} data-testid="check-availability-btn"
                    className="w-full flex items-center justify-center gap-2 rounded-2xl border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#B8912A] hover:text-black py-2.5 text-sm tracking-widest uppercase transition-colors disabled:opacity-50">
                    {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck className="w-4 h-4" />}
                    {t("listing.checkAvailability")}
                  </button>
                  {avail !== null && (
                    <div data-testid="availability-result" className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm ${avail ? "bg-emerald-950/50 text-emerald-300 border border-emerald-500/30" : "bg-red-950/50 text-red-300 border border-red-500/30"}`}>
                      {avail ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                      {avail ? t("listing.availableDates") : t("listing.unavailableDates")}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 space-y-3">
                <button onClick={() => openDialog("reservation")} data-testid="reserve-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-black font-semibold py-3.5 text-sm tracking-widest uppercase transition-colors">
                  <CalendarCheck className="w-4 h-4" /> {t("listing.reserveOnline")}
                </button>
                <button onClick={() => openDialog("quote")} data-testid="quote-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-2xl border border-white/10 text-[#F5F0E1] hover:border-[#D4AF37] py-3 text-sm tracking-widest uppercase transition-colors">
                  <FileText className="w-4 h-4" /> {t("listing.requestQuote")}
                </button>
                <a href={waLink(settings.whatsapp, waText)} target="_blank" rel="noreferrer" data-testid="whatsapp-btn"
                  className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium py-3 text-sm tracking-widest uppercase transition-colors">
                  <MessageCircle className="w-4 h-4" /> {t("listing.whatsappDirect")}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0E0D0A]/80 backdrop-blur-xl border border-[#D4AF37]/20 text-[#F5F0E1] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl text-[#F5F0E1]">
              {bookingType === "reservation" ? t("listing.dialogReserve") : t("listing.dialogQuote")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#93A099] text-sm -mt-2">{item.title} · {item.location}</p>
          <form onSubmit={submitBooking} className="space-y-4 mt-2">
            <input required placeholder={t("listing.fullName")} value={form.customer_name} data-testid="booking-name"
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37]" />
            <input required type="email" placeholder={t("listing.email")} value={form.email} data-testid="booking-email"
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37]" />
            <input placeholder={t("listing.phone")} value={form.phone} data-testid="booking-phone"
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37]" />
            {bookingType === "reservation" && (
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-3 text-sm text-[#F5F0E1] [color-scheme:dark] outline-none focus:border-[#D4AF37]" />
                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-3 py-3 text-sm text-[#F5F0E1] [color-scheme:dark] outline-none focus:border-[#D4AF37]" />
              </div>
            )}
            <input type="number" min="1" placeholder={t("listing.guestsPh")} value={form.guests} data-testid="booking-guests"
              onChange={(e) => setForm({ ...form, guests: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37]" />
            <textarea placeholder={t("listing.messagePh")} value={form.message} data-testid="booking-message" rows={3}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full bg-white/[0.04] border border-white/10 rounded-2xl px-4 py-3 text-sm text-[#F5F0E1] outline-none focus:border-[#D4AF37] resize-none" />
            <button type="submit" disabled={submitting} data-testid="booking-submit"
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] hover:bg-[#B8912A] text-black font-semibold py-3.5 text-sm tracking-widest uppercase transition-colors disabled:opacity-50">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {t("listing.sendRequest")}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
